import type { Bi, Gender, ReviewStatus } from '@/sanskrit/lib/sanskrit/types'

export type KridantaId = 'kta' | 'ktavatu' | 'shatr' | 'shanac' | 'tumun' | 'ktva' | 'lyap' | 'tavyat' | 'aniyar' | 'yat'

export interface KridantaPratyaya {
    id: KridantaId
    name: string
    iast: string
    kind: Bi
    meaning: Bi
    usage: Bi
    declinable: boolean
    example: { sa: string; hi: string; en: string }
}

export const KRIDANTA_PRATYAYAS: KridantaPratyaya[] = [
    {
        id: 'kta',
        name: 'क्त',
        iast: 'kta',
        kind: { hi: 'भूतकालिक कृदन्त (कर्मवाच्य)', en: 'Past passive participle' },
        meaning: { hi: 'किया गया, हुआ', en: '"done, gone": a completed action' },
        usage: {
            hi: 'विशेषण की तरह प्रयोग होता है और विशेष्य के लिङ्ग, विभक्ति, वचन के अनुसार बदलता है। अकर्मक धातुओं (गम्, स्था) के साथ कर्ता का बोध कराता है: सः गतः (वह गया)।',
            en: 'Used like an adjective and agrees with its noun. With intransitive roots (गम्, स्था) it describes the doer: सः गतः (he has gone).'
        },
        declinable: true,
        example: { sa: 'रामेण पुस्तकं पठितम्।', hi: 'राम द्वारा पुस्तक पढ़ी गई।', en: 'The book was read by Rāma.' }
    },
    {
        id: 'ktavatu',
        name: 'क्तवतु',
        iast: 'ktavatu',
        kind: { hi: 'भूतकालिक कृदन्त (कर्तृवाच्य)', en: 'Past active participle' },
        meaning: { hi: 'जिसने किया', en: '"one who has done"' },
        usage: {
            hi: 'कर्ता के लिङ्ग-वचन के अनुसार: सः पठितवान्, सा पठितवती। सरल भूतकाल के लिए बहुत उपयोगी।',
            en: 'Agrees with the doer: सः पठितवान् (he read), सा पठितवती (she read). A very handy way to talk about the past.'
        },
        declinable: true,
        example: { sa: 'बालिका पत्रं लिखितवती।', hi: 'बालिका ने पत्र लिखा।', en: 'The girl wrote a letter.' }
    },
    {
        id: 'shatr',
        name: 'शतृ',
        iast: 'śatṛ',
        kind: { hi: 'वर्तमानकालिक कृदन्त (परस्मैपद)', en: 'Present active participle (parasmaipada)' },
        meaning: { hi: 'करता हुआ', en: '"while doing"' },
        usage: {
            hi: 'दो क्रियाएँ एक साथ: बालकः पठन् गच्छति (बालक पढ़ता हुआ जाता है)।',
            en: 'Two actions at the same time: बालकः पठन् गच्छति (the boy walks while reading).'
        },
        declinable: true,
        example: { sa: 'बालकः हसन् धावति।', hi: 'बालक हँसता हुआ दौड़ता है।', en: 'The boy runs laughing.' }
    },
    {
        id: 'shanac',
        name: 'शानच्',
        iast: 'śānac',
        kind: { hi: 'वर्तमानकालिक कृदन्त (आत्मनेपद)', en: 'Present middle participle (ātmanepada)' },
        meaning: { hi: 'करता हुआ (आत्मनेपदी धातुओं से)', en: '"while doing", from ātmanepada verbs' },
        usage: {
            hi: 'आत्मनेपदी धातुओं से: लभमानः, सेवमानः। अ-अङ्ग के बाद मान, अन्यथा आन।',
            en: 'From ātmanepada verbs: लभमानः, सेवमानः. After an अ-stem it is -मान, otherwise -आन.'
        },
        declinable: true,
        example: { sa: 'पुत्रः पितरं सेवमानः गृहे वसति।', hi: 'पुत्र पिता की सेवा करता हुआ घर में रहता है।', en: 'The son lives at home, serving his father.' }
    },
    {
        id: 'tumun',
        name: 'तुमुन्',
        iast: 'tumun',
        kind: { hi: 'हेत्वर्थक कृदन्त (अव्यय)', en: 'Infinitive (indeclinable)' },
        meaning: { hi: 'करने के लिए', en: '"to do, in order to do"' },
        usage: {
            hi: 'प्रयोजन बताने के लिए, और शक्, इष् जैसी धातुओं के साथ: पठितुं गच्छति, पठितुं शक्नोति।',
            en: 'Expresses purpose, and is used with verbs like शक् and इष्: पठितुं गच्छति (goes to study), पठितुं शक्नोति (can read).'
        },
        declinable: false,
        example: { sa: 'अहं जलं पातुम् इच्छामि।', hi: 'मैं पानी पीना चाहता हूँ।', en: 'I want to drink water.' }
    },
    {
        id: 'ktva',
        name: 'क्त्वा',
        iast: 'ktvā',
        kind: { hi: 'पूर्वकालिक कृदन्त (अव्यय)', en: 'Absolutive / gerund (indeclinable)' },
        meaning: { hi: 'करके', en: '"having done"' },
        usage: {
            hi: 'जब एक ही कर्ता पहले एक काम करके फिर दूसरा करे। उपसर्ग न हो तभी क्त्वा।',
            en: 'When the same doer finishes one action before another. Used only when there is no prefix.'
        },
        declinable: false,
        example: { sa: 'सः पठित्वा क्रीडति।', hi: 'वह पढ़कर खेलता है।', en: 'Having studied, he plays.' }
    },
    {
        id: 'lyap',
        name: 'ल्यप्',
        iast: 'lyap',
        kind: { hi: 'पूर्वकालिक कृदन्त, उपसर्ग सहित (अव्यय)', en: 'Absolutive with a prefix (indeclinable)' },
        meaning: { hi: 'करके (उपसर्ग वाली धातु से)', en: '"having done", from a prefixed root' },
        usage: {
            hi: 'उपसर्ग होने पर क्त्वा के स्थान पर ल्यप् (य) आता है (समासेऽनञ्पूर्वे क्त्वो ल्यप् 7.1.37): प्रणम्य, उपविश्य।',
            en: 'With a prefix, क्त्वा is replaced by ल्यप् (-य) (समासेऽनञ्पूर्वे क्त्वो ल्यप् 7.1.37): प्रणम्य, उपविश्य.'
        },
        declinable: false,
        example: { sa: 'शिष्यः गुरुं प्रणम्य उपविशति।', hi: 'शिष्य गुरु को प्रणाम करके बैठता है।', en: 'Having bowed to the teacher, the student sits down.' }
    },
    {
        id: 'tavyat',
        name: 'तव्यत्',
        iast: 'tavyat',
        kind: { hi: 'कृत्य प्रत्यय (योग्यता/कर्तव्य)', en: 'Gerundive: "should be done"' },
        meaning: { hi: 'करना चाहिए, करने योग्य', en: '"to be done, ought to be done"' },
        usage: {
            hi: 'कर्मवाच्य में: मया पुस्तकं पठितव्यम् (मुझे पुस्तक पढ़नी चाहिए)।',
            en: 'In the passive sense: मया पुस्तकं पठितव्यम् (the book should be read by me).'
        },
        declinable: true,
        example: { sa: 'मया पुस्तकं पठितव्यम्।', hi: 'मुझे पुस्तक पढ़नी चाहिए।', en: 'I should read the book.' }
    },
    {
        id: 'aniyar',
        name: 'अनीयर्',
        iast: 'anīyar',
        kind: { hi: 'कृत्य प्रत्यय (योग्यता)', en: 'Gerundive: "worth doing"' },
        meaning: { hi: 'करने योग्य', en: '"worthy of being done"' },
        usage: {
            hi: 'तव्यत् जैसा अर्थ: दर्शनीयम् (देखने योग्य), पठनीयम् (पढ़ने योग्य)।',
            en: 'Close to तव्यत्: दर्शनीयम् (worth seeing), पठनीयम् (worth reading).'
        },
        declinable: true,
        example: { sa: 'उद्यानं दर्शनीयम् अस्ति।', hi: 'उद्यान देखने योग्य है।', en: 'The garden is worth seeing.' }
    },
    {
        id: 'yat',
        name: 'यत् / ण्यत्',
        iast: 'yat / ṇyat',
        kind: { hi: 'कृत्य प्रत्यय', en: 'Gerundive in -ya' },
        meaning: { hi: 'करने योग्य', en: '"to be done"' },
        usage: {
            hi: 'स्वरान्त धातुओं से प्रायः यत् (पेयम्, देयम्), और ऋकारान्त/हलन्त से ण्यत् (कार्यम्)।',
            en: 'Vowel-final roots usually take यत् (पेयम्, देयम्); others take ण्यत् (कार्यम्).'
        },
        declinable: true,
        example: { sa: 'जलं पेयम् अस्ति।', hi: 'पानी पीने योग्य है।', en: 'The water is fit to drink.' }
    }
]

export interface KridantaForm {
    form: string
    variants?: string[]
    dhatuId: string
    pratyaya: KridantaId
    gender?: Gender
    meaning: Bi
    status: ReviewStatus
    note?: Bi
}

const k = (form: string, dhatuId: string, pratyaya: KridantaId, hi: string, en: string, extra: Partial<KridantaForm> = {}): KridantaForm => ({
    form,
    dhatuId,
    pratyaya,
    meaning: { hi, en },
    status: 'verified',
    ...extra
})

export const KRIDANTA_FORMS: KridantaForm[] = [
    k('पठितः', 'path', 'kta', 'पढ़ा गया', 'read (was read)', { gender: 'm' }),
    k('पठितम्', 'path', 'kta', 'पढ़ा गया', 'read (neuter)', { gender: 'n' }),
    k('गतः', 'gam', 'kta', 'गया', 'gone', { gender: 'm' }),
    k('गता', 'gam', 'kta', 'गई', 'gone (fem.)', { gender: 'f' }),
    k('कृतः', 'kr', 'kta', 'किया गया', 'done, made', { gender: 'm' }),
    k('कृतम्', 'kr', 'kta', 'किया गया', 'done (neuter)', { gender: 'n' }),
    k('दृष्टः', 'drs', 'kta', 'देखा गया', 'seen', { gender: 'm' }),
    k('पीतः', 'pa', 'kta', 'पिया गया', 'drunk', { gender: 'm', note: { hi: 'पा का ई हो जाता है (घुमास्थागापाजहातिसां हलि 6.4.66)।', en: 'The आ of पा becomes ई (घुमास्थागापाजहातिसां हलि 6.4.66).' } }),
    k('स्थितः', 'stha', 'kta', 'स्थित, ठहरा हुआ', 'standing, situated', { gender: 'm' }),
    k('लिखितः', 'likh', 'kta', 'लिखा गया', 'written', { gender: 'm' }),
    k('लिखितम्', 'likh', 'kta', 'लिखा गया', 'written (neuter)', { gender: 'n' }),
    k('भूतः', 'bhu', 'kta', 'हुआ', 'become, been', { gender: 'm' }),
    k('खादितः', 'khad', 'kta', 'खाया गया', 'eaten', { gender: 'm' }),
    k('नतः', 'nam', 'kta', 'झुका हुआ', 'bowed', { gender: 'm' }),
    k('पक्वः', 'pac', 'kta', 'पका हुआ', 'cooked, ripe', { gender: 'm', note: { hi: 'पच् + क्त में त का व होता है (पचो वः 8.2.52): पक्वः।', en: 'With पच् the त of क्त becomes व (पचो वः 8.2.52): पक्वः.' } }),
    k('पक्वम्', 'pac', 'kta', 'पका हुआ', 'cooked, ripe (neuter)', { gender: 'n' }),
    k('हसितः', 'has', 'kta', 'हँसा', 'laughed', { gender: 'm' }),
    k('उदितः', 'vad', 'kta', 'कहा गया', 'spoken', { gender: 'm', note: { hi: 'वद् में सम्प्रसारण: व → उ (वचिस्वपियजादीनां किति 6.1.15)।', en: 'वद् shows samprasāraṇa, व → उ (वचिस्वपियजादीनां किति 6.1.15).' } }),
    k('क्रीडितः', 'krid', 'kta', 'खेला', 'played', { gender: 'm' }),
    k('सुप्तः', 'svap', 'kta', 'सोया हुआ', 'asleep', { gender: 'm', note: { hi: 'स्वप् में सम्प्रसारण: स्वप् → सुप् + त = सुप्तः।', en: 'स्वप् shows samprasāraṇa: स्वप् → सुप् + त = सुप्तः.' } }),
    k('मुक्तः', 'muc', 'kta', 'छूटा हुआ, मुक्त', 'released, free', { gender: 'm' }),
    k('आगतः', 'agam', 'kta', 'आया हुआ', 'arrived', { gender: 'm' }),
    k('उत्थितः', 'utstha', 'kta', 'उठा हुआ', 'risen', { gender: 'm' }),
    k('उपविष्टः', 'upavis', 'kta', 'बैठा हुआ', 'seated', { gender: 'm' }),

    k('पठितवान्', 'path', 'ktavatu', 'पढ़ा (पुरुष ने)', 'read (he read)', { gender: 'm' }),
    k('पठितवती', 'path', 'ktavatu', 'पढ़ा (स्त्री ने)', 'read (she read)', { gender: 'f' }),
    k('पठितवत्', 'path', 'ktavatu', 'पढ़ा (नपुं.)', 'read (neuter)', { gender: 'n' }),
    k('गतवान्', 'gam', 'ktavatu', 'गया', 'went (he went)', { gender: 'm' }),
    k('गतवती', 'gam', 'ktavatu', 'गई', 'went (she went)', { gender: 'f' }),
    k('कृतवान्', 'kr', 'ktavatu', 'किया', 'did (he did)', { gender: 'm' }),
    k('कृतवती', 'kr', 'ktavatu', 'किया (स्त्री ने)', 'did (she did)', { gender: 'f' }),
    k('दृष्टवान्', 'drs', 'ktavatu', 'देखा', 'saw (he saw)', { gender: 'm' }),
    k('पीतवान्', 'pa', 'ktavatu', 'पिया', 'drank (he drank)', { gender: 'm' }),
    k('लिखितवान्', 'likh', 'ktavatu', 'लिखा', 'wrote (he wrote)', { gender: 'm' }),
    k('लिखितवती', 'likh', 'ktavatu', 'लिखा (स्त्री ने)', 'wrote (she wrote)', { gender: 'f' }),
    k('खादितवान्', 'khad', 'ktavatu', 'खाया', 'ate (he ate)', { gender: 'm' }),
    k('हसितवान्', 'has', 'ktavatu', 'हँसा', 'laughed (he laughed)', { gender: 'm' }),

    k('पठन्', 'path', 'shatr', 'पढ़ता हुआ', 'reading (masc.)', { gender: 'm' }),
    k('पठन्ती', 'path', 'shatr', 'पढ़ती हुई', 'reading (fem.)', { gender: 'f' }),
    k('पठत्', 'path', 'shatr', 'पढ़ता हुआ (नपुं.)', 'reading (neut.)', { gender: 'n' }),
    k('गच्छन्', 'gam', 'shatr', 'जाता हुआ', 'going (masc.)', { gender: 'm' }),
    k('गच्छन्ती', 'gam', 'shatr', 'जाती हुई', 'going (fem.)', { gender: 'f' }),
    k('पश्यन्', 'drs', 'shatr', 'देखता हुआ', 'seeing (masc.)', { gender: 'm' }),
    k('कुर्वन्', 'kr', 'shatr', 'करता हुआ', 'doing (masc.)', { gender: 'm' }),
    k('कुर्वती', 'kr', 'shatr', 'करती हुई', 'doing (fem.)', { gender: 'f', note: { hi: 'अ-अङ्ग न होने से स्त्रीलिङ्ग में न् नहीं जुड़ता: कुर्वती।', en: 'Not an अ-stem, so the feminine has no न्: कुर्वती.' } }),
    k('पिबन्', 'pa', 'shatr', 'पीता हुआ', 'drinking (masc.)', { gender: 'm' }),
    k('तिष्ठन्', 'stha', 'shatr', 'ठहरता हुआ', 'standing (masc.)', { gender: 'm' }),
    k('भवन्', 'bhu', 'shatr', 'होता हुआ', 'being (masc.)', { gender: 'm' }),
    k('सन्', 'as', 'shatr', 'होता हुआ', 'being (masc.)', { gender: 'm' }),
    k('सती', 'as', 'shatr', 'होती हुई', 'being (fem.)', { gender: 'f' }),
    k('लिखन्', 'likh', 'shatr', 'लिखता हुआ', 'writing (masc.)', { gender: 'm' }),
    k('लिखन्ती', 'likh', 'shatr', 'लिखती हुई', 'writing (fem.)', { gender: 'f', variants: ['लिखती'], note: { hi: 'तुदादि धातु: स्त्रीलिङ्ग में लिखन्ती और लिखती दोनों स्वीकृत (आच्छीनद्योर्नुम् 7.1.80)।', en: 'A class 6 root: the feminine may be लिखन्ती or लिखती (आच्छीनद्योर्नुम् 7.1.80).' } }),
    k('खादन्', 'khad', 'shatr', 'खाता हुआ', 'eating (masc.)', { gender: 'm' }),
    k('हसन्', 'has', 'shatr', 'हँसता हुआ', 'laughing (masc.)', { gender: 'm' }),
    k('हसन्ती', 'has', 'shatr', 'हँसती हुई', 'laughing (fem.)', { gender: 'f' }),
    k('वदन्', 'vad', 'shatr', 'बोलता हुआ', 'speaking (masc.)', { gender: 'm' }),

    k('पचमानः', 'pac', 'shanac', 'पकाता हुआ', 'cooking (for oneself)', { gender: 'm' }),
    k('लभमानः', 'labh', 'shanac', 'पाता हुआ', 'obtaining', { gender: 'm' }),
    k('सेवमानः', 'sev', 'shanac', 'सेवा करता हुआ', 'serving', { gender: 'm' }),
    k('कुर्वाणः', 'kr', 'shanac', 'करता हुआ (आत्मनेपद)', 'doing (for oneself)', { gender: 'm', note: { hi: 'अ-अङ्ग नहीं, इसलिए आन (मान नहीं); र् के कारण न का ण।', en: 'Not an अ-stem, so -आन (not -मान); the र् turns न into ण.' } }),

    k('पठितुम्', 'path', 'tumun', 'पढ़ने के लिए', 'to read'),
    k('गन्तुम्', 'gam', 'tumun', 'जाने के लिए', 'to go'),
    k('कर्तुम्', 'kr', 'tumun', 'करने के लिए', 'to do'),
    k('द्रष्टुम्', 'drs', 'tumun', 'देखने के लिए', 'to see'),
    k('पातुम्', 'pa', 'tumun', 'पीने के लिए', 'to drink'),
    k('स्थातुम्', 'stha', 'tumun', 'ठहरने के लिए', 'to stay'),
    k('लेखितुम्', 'likh', 'tumun', 'लिखने के लिए', 'to write'),
    k('भवितुम्', 'bhu', 'tumun', 'होने के लिए', 'to be, to become'),
    k('खादितुम्', 'khad', 'tumun', 'खाने के लिए', 'to eat'),
    k('हसितुम्', 'has', 'tumun', 'हँसने के लिए', 'to laugh'),
    k('वदितुम्', 'vad', 'tumun', 'बोलने के लिए', 'to speak'),
    k('नन्तुम्', 'nam', 'tumun', 'झुकने के लिए', 'to bow'),
    k('क्रीडितुम्', 'krid', 'tumun', 'खेलने के लिए', 'to play'),
    k('पक्तुम्', 'pac', 'tumun', 'पकाने के लिए', 'to cook'),
    k('आगन्तुम्', 'agam', 'tumun', 'आने के लिए', 'to come'),

    k('पठित्वा', 'path', 'ktva', 'पढ़कर', 'having read'),
    k('गत्वा', 'gam', 'ktva', 'जाकर', 'having gone'),
    k('कृत्वा', 'kr', 'ktva', 'करके', 'having done'),
    k('दृष्ट्वा', 'drs', 'ktva', 'देखकर', 'having seen'),
    k('पीत्वा', 'pa', 'ktva', 'पीकर', 'having drunk'),
    k('स्थित्वा', 'stha', 'ktva', 'ठहरकर', 'having stayed'),
    k('लिखित्वा', 'likh', 'ktva', 'लिखकर', 'having written', { variants: ['लेखित्वा'], note: { hi: 'लिखित्वा और लेखित्वा दोनों स्वीकृत (1.2.26)।', en: 'Both लिखित्वा and लेखित्वा are accepted (1.2.26).' } }),
    k('भूत्वा', 'bhu', 'ktva', 'होकर', 'having become'),
    k('खादित्वा', 'khad', 'ktva', 'खाकर', 'having eaten'),
    k('हसित्वा', 'has', 'ktva', 'हँसकर', 'having laughed'),
    k('उदित्वा', 'vad', 'ktva', 'बोलकर', 'having spoken'),
    k('नत्वा', 'nam', 'ktva', 'झुककर', 'having bowed'),
    k('पक्त्वा', 'pac', 'ktva', 'पकाकर', 'having cooked'),
    k('क्रीडित्वा', 'krid', 'ktva', 'खेलकर', 'having played'),
    k('मिलित्वा', 'mil', 'ktva', 'मिलकर', 'having met, together', { variants: ['मेलित्वा'] }),

    k('आगम्य', 'agam', 'lyap', 'आकर', 'having come', { variants: ['आगत्य'], note: { hi: 'आगम्य और आगत्य दोनों स्वीकृत (वा ल्यपि 6.4.38)।', en: 'Both आगम्य and आगत्य are accepted (वा ल्यपि 6.4.38).' } }),
    k('प्रणम्य', 'pranam', 'lyap', 'प्रणाम करके', 'having bowed'),
    k('उपविश्य', 'upavis', 'lyap', 'बैठकर', 'having sat down'),
    k('अनुभूय', 'anubhu', 'lyap', 'अनुभव करके', 'having experienced'),
    k('उत्थाय', 'utstha', 'lyap', 'उठकर', 'having stood up'),
    k('प्रविश्य', 'pravis', 'lyap', 'प्रवेश करके', 'having entered'),
    k('विहस्य', 'vihas', 'lyap', 'मुस्कुराकर', 'having smiled'),

    k('पठितव्यम्', 'path', 'tavyat', 'पढ़ना चाहिए', 'should be read', { gender: 'n' }),
    k('गन्तव्यम्', 'gam', 'tavyat', 'जाना चाहिए', 'should be gone to', { gender: 'n' }),
    k('कर्तव्यम्', 'kr', 'tavyat', 'करना चाहिए, कर्तव्य', 'should be done; duty', { gender: 'n' }),
    k('द्रष्टव्यम्', 'drs', 'tavyat', 'देखना चाहिए', 'should be seen', { gender: 'n' }),
    k('पातव्यम्', 'pa', 'tavyat', 'पीना चाहिए', 'should be drunk', { gender: 'n' }),
    k('लेखितव्यम्', 'likh', 'tavyat', 'लिखना चाहिए', 'should be written', { gender: 'n' }),
    k('भवितव्यम्', 'bhu', 'tavyat', 'होना चाहिए', 'should be', { gender: 'n' }),

    k('पठनीयम्', 'path', 'aniyar', 'पढ़ने योग्य', 'worth reading', { gender: 'n' }),
    k('गमनीयम्', 'gam', 'aniyar', 'जाने योग्य', 'worth going to', { gender: 'n' }),
    k('करणीयम्', 'kr', 'aniyar', 'करने योग्य', 'worth doing', { gender: 'n' }),
    k('दर्शनीयम्', 'drs', 'aniyar', 'देखने योग्य', 'worth seeing', { gender: 'n' }),
    k('पानीयम्', 'pa', 'aniyar', 'पीने योग्य; पानी', 'drinkable; water', { gender: 'n' }),
    k('लेखनीयम्', 'likh', 'aniyar', 'लिखने योग्य', 'worth writing', { gender: 'n' }),

    k('पेयम्', 'pa', 'yat', 'पीने योग्य', 'fit to drink', { gender: 'n', note: { hi: 'आ का ई (ईद्यति 6.4.65) और गुण: पा + य = पेय।', en: 'आ becomes ई before यत् (ईद्यति 6.4.65), then guṇa: पा + य = पेय.' } }),
    k('देयम्', 'da', 'yat', 'देने योग्य', 'to be given', { gender: 'n' }),
    k('गेयम्', 'gai', 'yat', 'गाने योग्य', 'to be sung', { gender: 'n' }),
    k('कार्यम्', 'kr', 'yat', 'करने योग्य, काम', 'to be done; work', { gender: 'n', note: { hi: 'ण्यत् प्रत्यय: ऋ की वृद्धि आर्।', en: 'With ण्यत्, ऋ takes vṛddhi आर्.' } })
]

export interface TaddhitaExample {
    base: string
    pratyaya: string
    result: string
    meaning: Bi
    note: Bi
}

export const TADDHITA_EXAMPLES: TaddhitaExample[] = [
    { base: 'मनु', pratyaya: 'अण्', result: 'मानवः', meaning: { hi: 'मनु की सन्तान, मनुष्य', en: 'descendant of Manu, human' }, note: { hi: 'पहले स्वर की वृद्धि (अ → आ)।', en: 'The first vowel takes vṛddhi (अ → आ).' } },
    { base: 'शिव', pratyaya: 'अण्', result: 'शैवः', meaning: { hi: 'शिव का उपासक', en: 'devotee of Śiva' }, note: { hi: 'इ → ऐ (वृद्धि)।', en: 'इ → ऐ (vṛddhi).' } },
    { base: 'वसुदेव', pratyaya: 'अण्', result: 'वासुदेवः', meaning: { hi: 'वसुदेव के पुत्र (कृष्ण)', en: 'son of Vasudeva (Kṛṣṇa)' }, note: { hi: 'पहले स्वर की वृद्धि।', en: 'Vṛddhi of the first vowel.' } },
    { base: 'दशरथ', pratyaya: 'इञ्', result: 'दाशरथिः', meaning: { hi: 'दशरथ के पुत्र (राम)', en: 'son of Daśaratha (Rāma)' }, note: { hi: 'अपत्य अर्थ में इञ्।', en: 'इञ् in the sense "offspring of".' } },
    { base: 'धन', pratyaya: 'मतुप्', result: 'धनवान्', meaning: { hi: 'धनी', en: 'wealthy' }, note: { hi: 'मतुप् का म अ-अन्त शब्दों के बाद व हो जाता है।', en: 'After a stem in अ, the म of मतुप् becomes व.' } },
    { base: 'बुद्धि', pratyaya: 'मतुप्', result: 'बुद्धिमान्', meaning: { hi: 'बुद्धिमान्', en: 'intelligent' }, note: { hi: 'इ-अन्त शब्द के बाद मान्।', en: 'After an इ-stem it stays मान्.' } },
    { base: 'गुण', pratyaya: 'मतुप्', result: 'गुणवान्', meaning: { hi: 'गुणी', en: 'virtuous' }, note: { hi: 'म → व।', en: 'म → व.' } },
    { base: 'श्री', pratyaya: 'मतुप्', result: 'श्रीमान्', meaning: { hi: 'श्रीमान्, सम्पन्न', en: 'prosperous, illustrious' }, note: { hi: 'ई-अन्त के बाद मान्।', en: 'After ई it stays मान्.' } },
    { base: 'सुन्दर', pratyaya: 'ष्यञ्', result: 'सौन्दर्यम्', meaning: { hi: 'सुन्दरता', en: 'beauty' }, note: { hi: 'भाव अर्थ; उ → औ (वृद्धि)।', en: 'An abstract noun; उ → औ (vṛddhi).' } },
    { base: 'मधुर', pratyaya: 'ष्यञ्', result: 'माधुर्यम्', meaning: { hi: 'मिठास', en: 'sweetness' }, note: { hi: 'अ → आ (वृद्धि)।', en: 'अ → आ (vṛddhi).' } },
    { base: 'लघु', pratyaya: 'तल्', result: 'लघुता', meaning: { hi: 'छोटापन', en: 'smallness, lightness' }, note: { hi: 'तल् (ता) सदा स्त्रीलिङ्ग।', en: 'तल् (-ता) always gives a feminine noun.' } },
    { base: 'मनुष्य', pratyaya: 'त्व', result: 'मनुष्यत्वम्', meaning: { hi: 'मनुष्यता', en: 'humanity' }, note: { hi: 'त्व सदा नपुंसकलिङ्ग।', en: '-त्व always gives a neuter noun.' } },
    { base: 'धर्म', pratyaya: 'ठक्', result: 'धार्मिकः', meaning: { hi: 'धार्मिक', en: 'righteous, religious' }, note: { hi: 'ठ का इक, और वृद्धि।', en: 'ठ becomes इक, with vṛddhi.' } },
    { base: 'समाज', pratyaya: 'ठक्', result: 'सामाजिकः', meaning: { hi: 'सामाजिक', en: 'social' }, note: { hi: 'अ → आ (वृद्धि), ठ → इक।', en: 'अ → आ (vṛddhi), ठ → इक.' } },
    { base: 'वेद', pratyaya: 'ठक्', result: 'वैदिकः', meaning: { hi: 'वैदिक', en: 'Vedic' }, note: { hi: 'ए → ऐ (वृद्धि)।', en: 'ए → ऐ (vṛddhi).' } }
]

export interface UpasargaInfo {
    sa: string
    iast: string
    sense: Bi
}

/** The 22 upasargas (निस्/निर् and दुस्/दुर् are listed separately). */
export const UPASARGAS: UpasargaInfo[] = [
    { sa: 'प्र', iast: 'pra', sense: { hi: 'आगे, प्रकर्ष', en: 'forward, intensely' } },
    { sa: 'परा', iast: 'parā', sense: { hi: 'उल्टा, पीछे', en: 'away, back' } },
    { sa: 'अप', iast: 'apa', sense: { hi: 'दूर, हीन', en: 'away, off' } },
    { sa: 'सम्', iast: 'sam', sense: { hi: 'साथ, अच्छी तरह', en: 'together, completely' } },
    { sa: 'अनु', iast: 'anu', sense: { hi: 'पीछे, अनुसार', en: 'after, along' } },
    { sa: 'अव', iast: 'ava', sense: { hi: 'नीचे', en: 'down' } },
    { sa: 'निस्', iast: 'nis', sense: { hi: 'बाहर, बिना', en: 'out, without' } },
    { sa: 'निर्', iast: 'nir', sense: { hi: 'बाहर, बिना', en: 'out, without' } },
    { sa: 'दुस्', iast: 'dus', sense: { hi: 'कठिन, बुरा', en: 'bad, difficult' } },
    { sa: 'दुर्', iast: 'dur', sense: { hi: 'कठिन, बुरा', en: 'bad, difficult' } },
    { sa: 'वि', iast: 'vi', sense: { hi: 'विशेष, अलग', en: 'apart, specially' } },
    { sa: 'आङ्', iast: 'āṅ (आ)', sense: { hi: 'तक, उल्टा, ओर', en: 'towards, up to, reverse' } },
    { sa: 'नि', iast: 'ni', sense: { hi: 'नीचे, भीतर', en: 'down, into' } },
    { sa: 'अधि', iast: 'adhi', sense: { hi: 'ऊपर, श्रेष्ठ', en: 'over, above' } },
    { sa: 'अपि', iast: 'api', sense: { hi: 'भी, निकट', en: 'on, near' } },
    { sa: 'अति', iast: 'ati', sense: { hi: 'अधिक, पार', en: 'beyond, excessively' } },
    { sa: 'सु', iast: 'su', sense: { hi: 'अच्छा', en: 'well, good' } },
    { sa: 'उत्', iast: 'ut', sense: { hi: 'ऊपर', en: 'up' } },
    { sa: 'अभि', iast: 'abhi', sense: { hi: 'ओर, सामने', en: 'towards' } },
    { sa: 'प्रति', iast: 'prati', sense: { hi: 'उल्टा, प्रत्येक', en: 'back, against, each' } },
    { sa: 'परि', iast: 'pari', sense: { hi: 'चारों ओर', en: 'around' } },
    { sa: 'उप', iast: 'upa', sense: { hi: 'निकट', en: 'near, sub-' } }
]

export interface UpasargaExample {
    upasarga: string
    dhatuId: string
    baseDhatuId: string
    meaning: Bi
}

/** Prefix + root pairs for the builder; the resulting verb is generated from the prefixed root entry. */
export const UPASARGA_EXAMPLES: UpasargaExample[] = [
    { upasarga: 'आ', dhatuId: 'agam', baseDhatuId: 'gam', meaning: { hi: 'आना', en: 'to come' } },
    { upasarga: 'अव', dhatuId: 'avagam', baseDhatuId: 'gam', meaning: { hi: 'समझना', en: 'to understand' } },
    { upasarga: 'अधि', dhatuId: 'adhigam', baseDhatuId: 'gam', meaning: { hi: 'प्राप्त करना', en: 'to attain' } },
    { upasarga: 'निर्', dhatuId: 'nirgam', baseDhatuId: 'gam', meaning: { hi: 'बाहर जाना', en: 'to go out' } },
    { upasarga: 'प्रति', dhatuId: 'pratigam', baseDhatuId: 'gam', meaning: { hi: 'लौटना', en: 'to return' } },
    { upasarga: 'उत्', dhatuId: 'udgam', baseDhatuId: 'gam', meaning: { hi: 'उदय होना', en: 'to rise' } },
    { upasarga: 'अनु', dhatuId: 'anugam', baseDhatuId: 'gam', meaning: { hi: 'पीछे चलना', en: 'to follow' } },
    { upasarga: 'सम्', dhatuId: 'sangam', baseDhatuId: 'gam', meaning: { hi: 'मिलना', en: 'to meet' } },
    { upasarga: 'आ', dhatuId: 'ahr', baseDhatuId: 'hr', meaning: { hi: 'लाना', en: 'to bring' } },
    { upasarga: 'प्र', dhatuId: 'prahr', baseDhatuId: 'hr', meaning: { hi: 'प्रहार करना', en: 'to strike' } },
    { upasarga: 'सम्', dhatuId: 'samhr', baseDhatuId: 'hr', meaning: { hi: 'संहार करना', en: 'to withdraw, destroy' } },
    { upasarga: 'वि', dhatuId: 'vihr', baseDhatuId: 'hr', meaning: { hi: 'विहार करना', en: 'to roam for pleasure' } },
    { upasarga: 'परि', dhatuId: 'parihr', baseDhatuId: 'hr', meaning: { hi: 'त्यागना', en: 'to avoid' } },
    { upasarga: 'अनु', dhatuId: 'anubhu', baseDhatuId: 'bhu', meaning: { hi: 'अनुभव करना', en: 'to experience' } },
    { upasarga: 'प्र', dhatuId: 'prabhu', baseDhatuId: 'bhu', meaning: { hi: 'समर्थ होना', en: 'to arise, be able' } },
    { upasarga: 'सम्', dhatuId: 'sambhu', baseDhatuId: 'bhu', meaning: { hi: 'सम्भव होना', en: 'to be possible' } },
    { upasarga: 'परा', dhatuId: 'parabhu', baseDhatuId: 'bhu', meaning: { hi: 'हराना', en: 'to defeat' } },
    { upasarga: 'उप', dhatuId: 'upavis', baseDhatuId: 'vis', meaning: { hi: 'बैठना', en: 'to sit down' } },
    { upasarga: 'प्र', dhatuId: 'pravis', baseDhatuId: 'vis', meaning: { hi: 'प्रवेश करना', en: 'to enter' } },
    { upasarga: 'प्र', dhatuId: 'pranam', baseDhatuId: 'nam', meaning: { hi: 'प्रणाम करना', en: 'to bow' } },
    { upasarga: 'उत्', dhatuId: 'utstha', baseDhatuId: 'stha', meaning: { hi: 'उठना', en: 'to stand up' } },
    { upasarga: 'प्र', dhatuId: 'prastha', baseDhatuId: 'stha', meaning: { hi: 'प्रस्थान करना', en: 'to set out' } },
    { upasarga: 'उप', dhatuId: 'upahas', baseDhatuId: 'has', meaning: { hi: 'उपहास करना', en: 'to mock' } },
    { upasarga: 'वि', dhatuId: 'vihas', baseDhatuId: 'has', meaning: { hi: 'मुस्कुराना', en: 'to smile' } }
]

export const UPASARGA_VERSE = {
    sa: 'उपसर्गेण धात्वर्थो बलादन्यत्र नीयते।\nप्रहाराहारसंहारविहारपरिहारवत्॥',
    hi: 'उपसर्ग धातु के अर्थ को बलपूर्वक दूसरे अर्थ में ले जाता है, जैसे (हृ से) प्रहार, आहार, संहार, विहार, परिहार।',
    en: 'A prefix forcibly carries the root’s meaning elsewhere, as in (from हृ) प्रहार (strike), आहार (food), संहार (destruction), विहार (pleasure-walk), परिहार (avoidance).'
}
